/***************************************************************************/
"use client";
/***************************************************************************/

import React, { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const DemoModeContext = createContext<boolean>(false);

const DemoModeProvider: React.FC<{
  enabled: boolean;
  children: React.ReactNode;
}> = ({ enabled, children }) => {
  return (
    <DemoModeContext.Provider value={enabled}>
      {children}
    </DemoModeContext.Provider>
  );
};

const useIsDemoMode = () => {
  const isDemoMode = useContext(DemoModeContext);

  return isDemoMode;
};

const IfDemoMode: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isDemoMode = useContext(DemoModeContext);

  return isDemoMode ? children : null;
};

const withDemoMode = <TProps extends React.JSX.IntrinsicAttributes>(
  Component: React.ComponentType<TProps>,
) => {
  const WithDemoMode = (props: TProps) => {
    const isDemoMode = useContext(DemoModeContext);

    if (isDemoMode) {
      return <Component {...props} />;
    }

    return null;
  };

  return WithDemoMode;
};

const DemoModeIndicator: React.FC = () => {
  const { t: tRoot } = useTranslation("root");

  return (
    <IfDemoMode>
      <div className="fixed z-[60] top-0 left-0 right-0 flex flex-row justify-center border-t-2 border-warning">
        <Tooltip>
          <TooltipTrigger className="bg-warning text-white text-xs px-2 rounded-b capitalize">
            {tRoot("demoMode.demoMode")}
          </TooltipTrigger>
          <TooltipContent className="max-w-[600px]">
            {tRoot("demoMode.tooltip")}
          </TooltipContent>
        </Tooltip>
      </div>
    </IfDemoMode>
  );
};

export default DemoModeIndicator;

export {
  DemoModeContext,
  DemoModeProvider,
  IfDemoMode,
  useIsDemoMode,
  withDemoMode,
};
